import { Box, Stack, Typography } from '@mui/material';
import { GridColDef } from '@mui/x-data-grid-premium';
import { GuestJourneySankeyFlow } from 'api/reports-service/guest-journey-reports.types';
import IncDataGrid from 'components/inc-data-grid/inc-data-grid.component';
import MovementIndicator from 'components/movement-indicator/movement-indicator.component';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { GUEST_JOURNEY_DEFINITIONS } from '../guest-journey-definitions';

export type MovementTableProps = {
  /** Section header (e.g. "Incoming" / "Outgoing"). */
  title: string;
  /** Movement flows from the tab's sankey payload — one row per flow. */
  rows: GuestJourneySankeyFlow[];
  loading?: boolean;
  /**
   * Omit the Movement column entirely (Offer Impact "Incoming" — all inbound is
   * just "Redeemed", so there's no directional movement to show).
   */
  hideMovementColumn?: boolean;
  /**
   * Render the Movement column right after Stage instead of last (Offer Impact
   * "Outgoing", per story 357748: Stage, Movement, No. of Customers, …).
   */
  movementColumnFirst?: boolean;
  'data-testid'?: string;
};

const formatNumber = (value: number) => value.toLocaleString();
const formatCurrency = (value: number) =>
  new Intl.NumberFormat(undefined, {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 2,
  }).format(value);

const MovementTable = ({
  title,
  rows,
  loading = false,
  hideMovementColumn = false,
  movementColumnFirst = false,
  'data-testid': dataTestId = 'MovementTable',
}: MovementTableProps) => {
  const { t } = useTranslation();

  const columns: GridColDef<GuestJourneySankeyFlow>[] = useMemo(() => {
    const stage: GridColDef<GuestJourneySankeyFlow> = {
      field: 'stage',
      headerName: GUEST_JOURNEY_DEFINITIONS.movementStage.getLabel(t),
      flex: 1,
      minWidth: 140,
    };
    const count: GridColDef<GuestJourneySankeyFlow> = {
      field: 'count',
      headerName: GUEST_JOURNEY_DEFINITIONS.movementCustomersMoved.getLabel(t),
      flex: 1,
      minWidth: 140,
      valueFormatter: formatNumber,
    };
    const totalVisits: GridColDef<GuestJourneySankeyFlow> = {
      field: 'totalVisits',
      headerName: GUEST_JOURNEY_DEFINITIONS.movementVisits.getLabel(t),
      flex: 1,
      minWidth: 100,
      valueFormatter: formatNumber,
    };
    const totalSpendPerCustomer: GridColDef<GuestJourneySankeyFlow> = {
      field: 'totalSpendPerCustomer',
      headerName: GUEST_JOURNEY_DEFINITIONS.movementNetSales.getLabel(t),
      flex: 1,
      minWidth: 120,
      valueFormatter: formatCurrency,
    };
    const movement: GridColDef<GuestJourneySankeyFlow> = {
      field: 'movement',
      headerName: GUEST_JOURNEY_DEFINITIONS.movementMovement.getLabel(t),
      flex: 1,
      minWidth: 140,
      display: 'flex',
      sortable: false,
      renderCell: params => (
        <MovementIndicator
          movement={params.value}
          size="small"
          data-testid={`${dataTestId}.Movement.${params.id}`}
        />
      ),
    };

    if (hideMovementColumn)
      return [stage, count, totalVisits, totalSpendPerCustomer];
    if (movementColumnFirst)
      return [stage, movement, count, totalVisits, totalSpendPerCustomer];
    return [stage, count, totalVisits, totalSpendPerCustomer, movement];
  }, [t, dataTestId, hideMovementColumn, movementColumnFirst]);

  return (
    <Stack spacing={1} data-testid={dataTestId}>
      <Typography variant="h2" data-testid={`${dataTestId}.Header`}>
        {title}
      </Typography>
      <Box>
        <IncDataGrid
          data-testid={`${dataTestId}.Grid`}
          columns={columns}
          rows={rows}
          loading={loading}
          // The BE can return several outgoing rows for one stage (one per
          // movement outcome), so stage alone isn't unique — key on the
          // stage + movement pair, which is the real row identity.
          getRowId={row => `${row.stage}|${row.movement.text}`}
          disableColumnReorder
          disableColumnMenu
          disableRowSelectionOnClick
          autoHeight
          hideFooter
          initialState={{
            sorting: {
              sortModel: [{ field: 'count', sort: 'desc' }],
            },
          }}
        />
      </Box>
    </Stack>
  );
};

export default MovementTable;
